import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Euro, FileText, Download, Loader2, PenLine, Eye, Mail, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { fmtEUR, errMsg, openBlob } from './api';
import { DevisEmailDialog } from './DevisEmailDialog';
import { SignatureTracking } from './SignatureTracking';
import { PackProgress, usePackPreparation } from './PackProgress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DOCS = [
  { kind: 'pack', label: 'Pack complet — Devis ESS + CP/CG (un seul PDF)', file: 'Pack_DOS_DOMIUM' },
  { kind: 'devis-ess', label: 'Devis ESS — Document valant facture', file: 'Devis_ESS' },
  { kind: 'dossier-maitre', label: 'Dossier Maître — Conditions Particulières & Générales', file: 'Dossier_Maitre_CP_CG' },
];

const Stat = ({ label, value, accent, testId }) => (
  <div className={`rounded-lg p-3 ${accent ? 'bg-emerald-50' : 'bg-slate-50'}`} data-testid={testId}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`text-xl font-bold ${accent ? 'text-emerald-700' : 'text-slate-800'}`}>{value}</p>
  </div>
);

const DocRow = ({ doc, busy, onPreview, onDownload }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`ia-doc-row-${doc.kind}`}>
    <span className="flex items-center gap-2 text-sm text-slate-700"><FileText className="h-4 w-4 text-violet-500" /> {doc.label}</span>
    <div className="flex gap-1">
      <Button variant="ghost" size="sm" onClick={onPreview} disabled={!!busy} className="gap-1.5" data-testid={`ia-doc-preview-${doc.kind}`}>
        {busy === `p-${doc.kind}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Aperçu
      </Button>
      <Button variant="ghost" size="sm" onClick={onDownload} disabled={!!busy} className="gap-1.5" data-testid={`ia-doc-download-${doc.kind}`}>
        {busy === `d-${doc.kind}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Télécharger
      </Button>
    </div>
  </div>
);

export const DevisCard = ({ session, api, getAuthHeader, busy, prepTick, onSaveOverride, onSaveTaux, onGenerate, onSessionUpdate }) => {
  const c = session.calcul || {};
  const [override, setOverride] = useState(session.contribution_override_ht ?? '');
  const [taux, setTaux] = useState(session.taux_override ?? '');
  useEffect(() => setTaux(session.taux_override ?? ''), [session.taux_override]);
  const [dl, setDl] = useState(null);
  const [signing, setSigning] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  useEffect(() => setOverride(session.contribution_override_ht ?? ''), [session.contribution_override_ht]);
  const ref = session.simulation_reference;
  const { etat: prep, prepare } = usePackPreparation(api, session.id, async () => onSessionUpdate(await api.get(session.id)));
  const generer = async () => { if (await onGenerate()) prepare(); };
  useEffect(() => { if (prepTick && ref) prepare(); }, [prepTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const preparing = !!prep && Object.values(prep).some((v) => v.status === 'en_cours');
  const definitive = (session.pack_versions || []).find((v) => v.statut === 'definitif');
  const verrou = busy || !!definitive;

  const fetchDoc = async (doc, mode) => {
    setDl(`${mode}-${doc.kind}`);
    try {
      const src = doc.kind === 'pack' ? `${API}/domium-ia/sessions/${session.id}/pack` : `${API}/domium/documents/${ref}/${doc.kind}`;
      const r = await axios.get(src, { headers: getAuthHeader(), responseType: 'blob' });
      if (mode === 'p') { openBlob(r.data, 'application/pdf'); return; }
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = Object.assign(document.createElement('a'), { href: url, download: `${doc.file}_${ref}.pdf` });
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error(`Erreur : ${doc.label}`); } finally { setDl(null); }
  };
  const sign = async () => {
    if (!window.confirm(`Créer la souscription officielle et envoyer le code de signature électronique à ${session.form?.identite?.email || "l'email du demandeur"} ?`)) return;
    setSigning(true);
    try {
      await axios.post(`${API}/domium/signature/initiate`, { reference: ref, signer_email: session.form?.identite?.email || undefined, method: 'email' }, { headers: getAuthHeader() });
      toast.success('Signature électronique initiée : code envoyé au demandeur');
      onSessionUpdate(await api.get(session.id));
    } catch (e) { toast.error(errMsg(e, 'Erreur envoi signature')); } finally { setSigning(false); }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="ia-devis-card">
      <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Euro className="h-4 w-4 text-emerald-600" /> 3. Devis ESS + CP/CG</h4>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Taux final" value={c.taux_final != null ? `${Number(c.taux_final).toFixed(2)} %` : '—'} testId="ia-taux-final" />
        <Stat label="Contribution HT" value={fmtEUR(c.contribution_ht)} testId="ia-contribution-ht" />
        <Stat label="Contribution TTC" value={fmtEUR(c.contribution_ttc)} accent testId="ia-contribution-ttc" />
        <Stat label="Garantie" value={`${session.garantie?.duree_ans || 10} ans`} testId="ia-garantie" />
      </div>
      {c.contribution_rectifiee && <p className="text-xs text-amber-700" data-testid="ia-contribution-rectifiee">Montant rectifié manuellement (calculé : {fmtEUR(c.contribution_calculee_ht)})</p>}
      {c.taux_rectifie && <p className="text-xs text-amber-700" data-testid="ia-taux-rectifie">Taux rectifié manuellement (calculé : {Number(c.taux_calcule || 0).toFixed(2)} % — {fmtEUR(c.contribution_calculee_ht)})</p>}
      {definitive && (
        <p className="flex items-center gap-1.5 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="ia-verrou-definitif">
          <Lock className="h-3.5 w-3.5" /> V{definitive.numero} définitive : taux et montant verrouillés. Retirez le définitif dans le panneau de gauche pour les modifier.
        </p>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-600 space-y-1">
          <span>Rectifier le taux à appliquer (%) — optionnel</span>
          <Input value={taux} disabled={!!definitive} onChange={(e) => setTaux(e.target.value)} placeholder={`Calculé : ${Number(c.taux_calcule ?? c.taux_final ?? 0).toFixed(2)} %`} className="h-9 w-64" data-testid="ia-taux-input" />
        </label>
        <Button variant="outline" size="sm" onClick={() => onSaveTaux(taux === '' ? null : taux)} disabled={verrou} className="h-9" data-testid="ia-taux-apply-btn">Appliquer</Button>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-600 space-y-1">
          <span>Rectifier le montant de la contribution (€ HT) — optionnel</span>
          <Input value={override} disabled={!!definitive} onChange={(e) => setOverride(e.target.value)} placeholder={`Calculé : ${c.contribution_calculee_ht ?? c.contribution_ht ?? 0} €`} className="h-9 w-64" data-testid="ia-override-input" />
        </label>
        <Button variant="outline" size="sm" onClick={() => onSaveOverride(override === '' ? null : override)} disabled={verrou} className="h-9" data-testid="ia-override-apply-btn">Appliquer</Button>
      </div>
      <Button onClick={generer} disabled={busy || preparing} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="ia-generate-pack-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} {ref ? `Mettre à jour le pack (${ref})` : 'Générer le pack (Devis + CP/CG)'}
      </Button>
      {ref && (
        <>
          <p className="text-xs text-slate-500" data-testid="ia-simulation-ref">Réf. {ref}</p>
          <PackProgress etat={prep} />
          <div className="space-y-2">
            {DOCS.map((d) => <DocRow key={d.kind} doc={d} busy={dl} onPreview={() => fetchDoc(d, 'p')} onDownload={() => fetchDoc(d, 'd')} />)}
          </div>
          <SignatureTracking tracking={session.signature} etat={session.etat_attestation} />
          {session.signature?.paiement === 'partiel' && (
            <Button size="sm" variant="outline" onClick={async () => {
              try { const r = await api.rappelEcheance(session.id); toast.success(`Rappel de l'échéance ${r.echeance}/4 envoyé à ${r.email} (lien valable 7 jours)`); }
              catch (e) { toast.error(e.response?.data?.detail || 'Rappel impossible'); }
            }} className="gap-1.5 border-emerald-300 text-emerald-700" data-testid="ia-rappel-echeance-btn">
              Envoyer le rappel de la prochaine échéance
            </Button>
          )}
          {session.devis_url && (
            <p className="text-[11px] text-slate-500">Lien de consultation suivi : <a href={session.devis_url} target="_blank" rel="noreferrer" className="text-violet-600 underline break-all" data-testid="ia-devis-url">{session.devis_url}</a></p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setEmailOpen(true)} className="bg-violet-600 hover:bg-violet-700 text-white gap-2" data-testid="ia-prepare-email-btn">
              <Mail className="h-4 w-4" /> Préparer l'email d'envoi
            </Button>
            <Button variant="outline" onClick={sign} disabled={signing || session.signature?.etat === 'signe'} className="gap-2" data-testid="ia-sign-btn">
              {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />} Créer la souscription officielle (signature électronique)
            </Button>
          </div>
          <DevisEmailDialog open={emailOpen} onOpenChange={setEmailOpen} session={session} api={api} onSent={onSessionUpdate} />
        </>
      )}
    </section>
  );
};
